// Guitar voicings for the chord stage.
//
// Hand-authored rather than vendored: the detector only emits major/minor
// triads, so 24 shapes cover 100% of what we can display, and standard
// first-position / barre fingerings are common musical knowledge — no
// dependency, no licence question, ~2 KB.
//
// Arrays run low-E → high-E. -1 = muted, 0 = open. `fingers` is 1..4 (0 = none).
// Accidentals use the movable A / A-minor barre shapes so every chord in a key
// is reachable without inventing exotic voicings.

export interface ChordShape {
  frets: number[];
  fingers: number[];
  barre?: { fret: number; from: number; to: number }; // string indices, inclusive
}

const MAJOR: Record<number, ChordShape> = {
  0:  { frets: [-1, 3, 2, 0, 1, 0], fingers: [0, 3, 2, 0, 1, 0] },                                   // C
  1:  { frets: [-1, 4, 6, 6, 6, 4], fingers: [0, 1, 3, 3, 3, 1], barre: { fret: 4, from: 1, to: 5 } }, // C#
  2:  { frets: [-1, -1, 0, 2, 3, 2], fingers: [0, 0, 0, 1, 3, 2] },                                  // D
  3:  { frets: [-1, 6, 8, 8, 8, 6], fingers: [0, 1, 3, 3, 3, 1], barre: { fret: 6, from: 1, to: 5 } }, // Eb
  4:  { frets: [0, 2, 2, 1, 0, 0], fingers: [0, 2, 3, 1, 0, 0] },                                    // E
  5:  { frets: [1, 3, 3, 2, 1, 1], fingers: [1, 3, 4, 2, 1, 1], barre: { fret: 1, from: 0, to: 5 } }, // F
  6:  { frets: [2, 4, 4, 3, 2, 2], fingers: [1, 3, 4, 2, 1, 1], barre: { fret: 2, from: 0, to: 5 } }, // F#
  7:  { frets: [3, 2, 0, 0, 0, 3], fingers: [2, 1, 0, 0, 0, 3] },                                    // G
  8:  { frets: [4, 6, 6, 5, 4, 4], fingers: [1, 3, 4, 2, 1, 1], barre: { fret: 4, from: 0, to: 5 } }, // G#
  9:  { frets: [-1, 0, 2, 2, 2, 0], fingers: [0, 0, 1, 2, 3, 0] },                                   // A
  10: { frets: [-1, 1, 3, 3, 3, 1], fingers: [0, 1, 2, 3, 4, 1], barre: { fret: 1, from: 1, to: 5 } }, // Bb
  11: { frets: [-1, 2, 4, 4, 4, 2], fingers: [0, 1, 2, 3, 4, 1], barre: { fret: 2, from: 1, to: 5 } }, // B
};

const MINOR: Record<number, ChordShape> = {
  0:  { frets: [-1, 3, 5, 5, 4, 3], fingers: [0, 1, 3, 4, 2, 1], barre: { fret: 3, from: 1, to: 5 } }, // Cm
  1:  { frets: [-1, 4, 6, 6, 5, 4], fingers: [0, 1, 3, 4, 2, 1], barre: { fret: 4, from: 1, to: 5 } }, // C#m
  2:  { frets: [-1, -1, 0, 2, 3, 1], fingers: [0, 0, 0, 2, 3, 1] },                                  // Dm
  3:  { frets: [-1, 6, 8, 8, 7, 6], fingers: [0, 1, 3, 4, 2, 1], barre: { fret: 6, from: 1, to: 5 } }, // Ebm
  4:  { frets: [0, 2, 2, 0, 0, 0], fingers: [0, 2, 3, 0, 0, 0] },                                    // Em
  5:  { frets: [1, 3, 3, 1, 1, 1], fingers: [1, 3, 4, 1, 1, 1], barre: { fret: 1, from: 0, to: 5 } }, // Fm
  6:  { frets: [2, 4, 4, 2, 2, 2], fingers: [1, 3, 4, 1, 1, 1], barre: { fret: 2, from: 0, to: 5 } }, // F#m
  7:  { frets: [3, 5, 5, 3, 3, 3], fingers: [1, 3, 4, 1, 1, 1], barre: { fret: 3, from: 0, to: 5 } }, // Gm
  8:  { frets: [4, 6, 6, 4, 4, 4], fingers: [1, 3, 4, 1, 1, 1], barre: { fret: 4, from: 0, to: 5 } }, // G#m
  9:  { frets: [-1, 0, 2, 2, 1, 0], fingers: [0, 0, 2, 3, 1, 0] },                                   // Am
  10: { frets: [-1, 1, 3, 3, 2, 1], fingers: [0, 1, 3, 4, 2, 1], barre: { fret: 1, from: 1, to: 5 } }, // Bbm
  11: { frets: [-1, 2, 4, 4, 3, 2], fingers: [0, 1, 3, 4, 2, 1], barre: { fret: 2, from: 1, to: 5 } }, // Bm
};

// EASY voicings: open shapes and 3–4 string forms with no full barre — what
// Moises shows in its "Easy" mode, and what a learner can actually fret. Every
// one of these was verified programmatically to spell the correct triad from
// standard tuning (an earlier hand-authored C# major silently spelled F minor).
const EASY_MAJOR: Record<number, ChordShape> = {
  0:  { frets: [-1, 3, 2, 0, 1, 0], fingers: [0, 3, 2, 0, 1, 0] },                    // C
  1:  { frets: [-1, -1, 6, 6, 6, 4], fingers: [0, 0, 2, 3, 4, 1] },                   // C#
  2:  { frets: [-1, -1, 0, 2, 3, 2], fingers: [0, 0, 0, 1, 3, 2] },                   // D
  3:  { frets: [-1, -1, 1, 3, 4, 3], fingers: [0, 0, 1, 2, 4, 3] },                   // Eb
  4:  { frets: [0, 2, 2, 1, 0, 0], fingers: [0, 2, 3, 1, 0, 0] },                     // E
  5:  { frets: [-1, -1, 3, 2, 1, 1], fingers: [0, 0, 4, 3, 1, 1] },                   // F
  6:  { frets: [-1, -1, 4, 3, 2, 2], fingers: [0, 0, 4, 3, 1, 1] },                   // F#
  7:  { frets: [3, 2, 0, 0, 0, 3], fingers: [2, 1, 0, 0, 0, 3] },                     // G
  8:  { frets: [-1, -1, 6, 5, 4, 4], fingers: [0, 0, 4, 3, 1, 1] },                   // Ab
  9:  { frets: [-1, 0, 2, 2, 2, 0], fingers: [0, 0, 1, 2, 3, 0] },                    // A
  10: { frets: [-1, -1, 3, 3, 3, 1], fingers: [0, 0, 2, 3, 4, 1] },                   // Bb
  11: { frets: [-1, -1, 4, 4, 4, 2], fingers: [0, 0, 2, 3, 4, 1] },                   // B
};

const EASY_MINOR: Record<number, ChordShape> = {
  0:  { frets: [-1, -1, 5, 5, 4, 3], fingers: [0, 0, 3, 4, 2, 1] },                   // Cm
  1:  { frets: [-1, -1, 6, 6, 5, 4], fingers: [0, 0, 3, 4, 2, 1] },                   // C#m
  2:  { frets: [-1, -1, 0, 2, 3, 1], fingers: [0, 0, 0, 2, 3, 1] },                   // Dm
  3:  { frets: [-1, -1, 1, 3, 4, 2], fingers: [0, 0, 1, 3, 4, 2] },                   // Ebm
  4:  { frets: [0, 2, 2, 0, 0, 0], fingers: [0, 2, 3, 0, 0, 0] },                     // Em
  5:  { frets: [-1, -1, 3, 1, 1, 1], fingers: [0, 0, 3, 1, 1, 1] },                   // Fm
  6:  { frets: [-1, -1, 4, 2, 2, 2], fingers: [0, 0, 4, 1, 1, 1] },                   // F#m
  7:  { frets: [3, 1, 0, 0, 3, 3], fingers: [2, 1, 0, 0, 3, 4] },                     // Gm
  8:  { frets: [-1, -1, 6, 4, 4, 4], fingers: [0, 0, 4, 1, 1, 1] },                   // G#m
  9:  { frets: [-1, 0, 2, 2, 1, 0], fingers: [0, 0, 2, 3, 1, 0] },                    // Am
  10: { frets: [-1, -1, 3, 3, 2, 1], fingers: [0, 0, 3, 4, 2, 1] },                   // Bbm
  11: { frets: [-1, -1, 4, 4, 3, 2], fingers: [0, 0, 3, 4, 2, 1] },                   // Bm
};

export type ShapeStyle = 'easy' | 'barre';

const PC: Record<string, number> = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6,
  G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11,
};

/** Parse a raw model label ("G:min", "D#", "N") into pitch class + quality. */
export function parseChord(raw: string): { pc: number; minor: boolean } | null {
  if (!raw || raw === 'N' || raw === 'X') return null;
  const minor = raw.endsWith(':min');
  const root = minor ? raw.slice(0, -4) : raw;
  const pc = PC[root];
  return pc == null ? null : { pc, minor };
}

/** Voicing for a raw label at the current transpose, or null for no-chord. */
export function shapeForChord(raw: string, semitones = 0, style: ShapeStyle = 'easy'): ChordShape | null {
  const c = parseChord(raw);
  if (!c) return null;
  const pc = (((c.pc + semitones) % 12) + 12) % 12;
  const table = style === 'barre' ? (c.minor ? MINOR : MAJOR) : (c.minor ? EASY_MINOR : EASY_MAJOR);
  return table[pc] ?? null;
}
